import React, { useState } from 'react';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import Drawer from '@mui/material/Drawer';
import Typography from '@mui/material/Typography';
import ErrorBoundary from 'components/ErrorBoundary/ErrorBoundary';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import Button from '@mui/material/Button';
import {
    sendScheduledDashboardEmail,
    sendScheduledDashboardEmailNow,
} from 'helpers/dashboardHelpers';
import ScheduledDashboardEmailConfigForm from 'components/DashboardSendModal/ScheduledDashboardEmailConfigForm/ScheduledDashboardEmailConfigForm';
import { ScheduledDashboardEmail } from 'components/DashboardSendModal/ScheduledDashboardEmailConfigForm/types';
import MessagePreview from 'components/DashboardSendModal/MessagePreview/MessagePreview';
import ScheduledDashboardEmailList from 'components/DashboardSendModal/ScheduledDashboardEmailList/ScheduledDashboardEmailList';
import { getTodayDateString } from 'components/inputs/dateHelpers';
import { shouldSendEmailRightAway } from 'components/DashboardSendModal/helpers';
import { DashboardT } from 'business/definitions';
import { trackEvent } from 'helpers/trackerHelpers';
import { useSelector } from 'store/hooks';
import { styled } from '@mui/material';
import WidgetTruncatedWarningContext from 'components/Contexts/WidgetTruncatedWarningContext';
import { find } from 'lodash';
import {
    useDeleteScheduleEmailMutation,
    useEditScheduleDashboardEmailConfigMutation,
    useGetScheduledDashboardEmails,
} from './hooks/scheduleDashboards';

type Props = {
    dashboard: DashboardT;
    show: boolean;
    closeModal: () => void;
};

const reduceScheduledDashboards = (
    scheduleDashboards: ScheduledDashboardEmail[] | undefined,
    reduceValue: 'config' | 'sender',
) =>
    scheduleDashboards
        ? scheduleDashboards.reduce((accumulator, current) => {
              if (!current.id) return accumulator;
              return {
                  ...accumulator,
                  [current.id]: current[reduceValue],
              };
          }, {})
        : {};

// TODO: refactoring opportunity with IndicatorAlertModal
const StyDrawer = styled(Drawer, {
    name: 'StyDrawer',
})({
    '& .MuiPaper-root': {
        width: '32vw',
        padding: '1vh 1vw',
    },
});

const StyTitle = styled(Typography, {
    name: 'StyTitle',
})({
    display: 'inline',
});

const StyCloseIcon = styled(CloseIcon, {
    name: 'StyCloseIcon',
})({
    float: 'right',
    cursor: 'pointer',
});

const drawerContentTextStyles = {
    fontWeight: 'lighter',
    color: '#666',
    fontSize: '1.1em',
    lineHeight: '1.5em',
    paddingTop: '1vh',
    paddingBottom: '3vh',
};

const StyDrawerContentText = styled(Typography, {
    name: 'StyDrawerContentText',
})(drawerContentTextStyles);

const StyEmailStatusMessage = styled('div', {
    name: 'StyEmailStatusMessage',
})({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '3vh 0',
});

const StyScheduledDashboardEmailList = styled(ScheduledDashboardEmailList, {
    name: 'StyScheduledDashboardEmailList',
})({
    '& .mui-no-schedule': drawerContentTextStyles,
});

const DashboardSendModal: React.FC<Props> = ({
    dashboard,
    show,
    closeModal,
}) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();

    const [isFormOpen, setIsFormOpen] = useState(false);
    const [showSendEmailButton, setShowSendEmailButton] = useState(true);
    const loggedInUserEmail = useSelector((state) => state.vision.user.email);
    const user = useSelector((state) => state.vision.user);
    const isSupportUser = useSelector((state) => state.vision.isSupportUser);
    const { shouldShowWidgetTruncatedWarning: isWidgetTruncated } =
        React.useContext(WidgetTruncatedWarningContext);

    const initialScheduledEmail: ScheduledDashboardEmail = {
        sender: user.email,
        senderId: user.id,
        config: {
            shouldSendCopyToSender: true,
            recipients: [loggedInUserEmail],
            text: '',
            firstSentDate: getTodayDateString(),
            recurrence: 'once' as const,
        },
    };
    const [scheduleEmail, setScheduleEmail] = useState<ScheduledDashboardEmail>(
        initialScheduledEmail,
    );
    const [openMessagePreview, setOpenMessagePreview] = useState(false);
    const [isEmailSchedulingSent, setIsEmailSchedulingSent] = useState(false);
    const dashboardKey = dashboard.key;
    if (dashboardKey === undefined) {
        throw new Error(
            'Dashboard key should be defined ine DashboardSendModal',
        );
    }
    const { data: userScheduledDashboards, isLoading } =
        useGetScheduledDashboardEmails(dashboardKey, 'self', isSupportUser);
    const { data: otherScheduledDashboards, isLoading: isOtherLoading } =
        useGetScheduledDashboardEmails(dashboardKey, 'other', isSupportUser);

    const addScheduleConfigMutation = useMutation({
        mutationFn: () =>
            sendScheduledDashboardEmail(dashboardKey, scheduleEmail),

        onSuccess: (response) => {
            // response can be null in the case where no scheduling was needed
            if (response !== null && userScheduledDashboards !== null) {
                queryClient.invalidateQueries({
                    queryKey: ['scheduled_dashboard_emails', dashboardKey],
                });
            }
        },
    });

    const deleteScheduleEmailMutation = useDeleteScheduleEmailMutation();

    const showMessagePreview = () => {
        setOpenMessagePreview(true);
        setIsFormOpen(false);
    };
    const sendMailNow = shouldSendEmailRightAway(scheduleEmail.config);
    const sendMailLater = scheduleEmail.config.recurrence !== 'once';
    const editMutation =
        useEditScheduleDashboardEmailConfigMutation(dashboardKey);

    const onEditScheduleConfig = () => {
        if (sendMailNow) {
            sendScheduledDashboardEmailNow(dashboardKey, scheduleEmail);
        }
        editMutation.mutate(scheduleEmail);
        setShowSendEmailButton(true);
    };

    const onSubmitScheduleConfig = () => {
        // FIXME: properly inform the user in case of error
        addScheduleConfigMutation.mutate();
        setOpenMessagePreview(false);
        setShowSendEmailButton(true);
        setIsEmailSchedulingSent(true);
        trackEvent(
            'decision/dashboard/options/send/nouvel-envoi/validé-click',
            {
                dashboard: dashboard.key,
                recurrence: scheduleEmail.config.recurrence,
            },
        );
    };

    return (
        <ErrorBoundary>
            <StyDrawer variant="persistent" anchor="right" open={show}>
                <div>
                    <StyTitle variant="h4">
                        {t('sendDashboard.modalTitle')}
                    </StyTitle>
                    <StyCloseIcon onClick={closeModal} fontSize="large" />
                </div>
                <StyDrawerContentText>
                    {t('sendDashboard.modalText')}
                </StyDrawerContentText>
                {isWidgetTruncated && (
                    <Typography variant="body1SemiBold" sx={{ mb: '1rem' }}>
                        {t('widget.widgetTruncatedWarning')}
                    </Typography>
                )}
                <div>
                    {showSendEmailButton && (
                        <Button
                            variant="contained"
                            onClick={() => {
                                setIsFormOpen(true);
                                setShowSendEmailButton(false);
                                setIsEmailSchedulingSent(false);
                                setScheduleEmail(initialScheduledEmail);
                                trackEvent(
                                    'decision/dashboard/options/send/nouvel-envoi-click',
                                    {
                                        dashboard: dashboard.key,
                                    },
                                );
                            }}
                        >
                            {t('sendDashboard.buttonNewShip')}
                        </Button>
                    )}
                </div>

                {isFormOpen && (
                    <ScheduledDashboardEmailConfigForm
                        onSubmit={() => {
                            if (scheduleEmail.id) {
                                onEditScheduleConfig();
                                setIsFormOpen(false);
                            } else {
                                showMessagePreview();
                            }
                        }}
                        onGoBack={() => {
                            setIsFormOpen(false);
                            setShowSendEmailButton(true);
                            setScheduleEmail(initialScheduledEmail);
                        }}
                        scheduledEmail={scheduleEmail}
                        setScheduledEmail={setScheduleEmail}
                        dashboard={dashboard}
                    />
                )}

                {isEmailSchedulingSent && sendMailNow && !sendMailLater && (
                    <StyEmailStatusMessage>
                        {t('sendDashboard.emailSent')}
                    </StyEmailStatusMessage>
                )}
                {isEmailSchedulingSent && (!sendMailNow || sendMailLater) && (
                    <StyEmailStatusMessage>
                        {t('sendDashboard.emailSchedulingSent')}
                    </StyEmailStatusMessage>
                )}
                {openMessagePreview && (
                    <MessagePreview
                        config={scheduleEmail.config}
                        sendMailNow={sendMailNow && !sendMailLater}
                        scheduledInTheFuture={sendMailLater}
                        onGoBack={() => {
                            setIsFormOpen(true);
                            setOpenMessagePreview(false);
                            setShowSendEmailButton(false);
                        }}
                        onSubmit={onSubmitScheduleConfig}
                    />
                )}
                <StyScheduledDashboardEmailList
                    configList={reduceScheduledDashboards(
                        userScheduledDashboards,
                        'config',
                    )}
                    isLoading={isLoading}
                    onDeleteScheduleConfig={(sdeId) =>
                        deleteScheduleEmailMutation.mutate({ id: sdeId })
                    }
                    onEditScheduleConfig={(sdeId) => {
                        const scheduled = find(
                            userScheduledDashboards,
                            (sde) => sde.id?.toString() === sdeId.toString(),
                        );
                        if (scheduled === undefined) {
                            return;
                        }

                        setScheduleEmail(scheduled);
                        setShowSendEmailButton(false);
                        setIsFormOpen(true);
                    }}
                    listTitle={t(
                        'sendDashboard.scheduledDashboardEmailListTitle',
                    )}
                    noSchedule={t('sendDashboard.noScheduledDashboardEmail')}
                />
                {isSupportUser && (
                    <StyScheduledDashboardEmailList
                        configList={reduceScheduledDashboards(
                            otherScheduledDashboards,
                            'config',
                        )}
                        senderList={reduceScheduledDashboards(
                            otherScheduledDashboards,
                            'sender',
                        )}
                        isLoading={isOtherLoading}
                        onEditScheduleConfig={(sdeId) => {
                            const scheduled = find(
                                otherScheduledDashboards,
                                (sde) =>
                                    sde.id?.toString() === sdeId.toString(),
                            );
                            if (scheduled === undefined) {
                                return;
                            }

                            setScheduleEmail(scheduled);
                            setShowSendEmailButton(false);
                            setIsFormOpen(true);
                        }}
                        onDeleteScheduleConfig={(sdeId) =>
                            deleteScheduleEmailMutation.mutate({ id: sdeId })
                        }
                        listTitle={t(
                            'sendDashboard.scheduledOtherDashboardEmailListTitle',
                        )}
                        noSchedule={t(
                            'sendDashboard.noScheduledDashboardEmail',
                        )}
                    />
                )}
            </StyDrawer>
        </ErrorBoundary>
    );
};

export default DashboardSendModal;
